import React, { FC, useCallback } from "react";

import { IntegrationType } from "generated/graphql";
import { existsAndNotEmpty } from "stentor-utils";

import { OnboardingProps } from "components/Onboarding/models";
import { AppIntegrationDetails } from "models";

import { IntegrationConnectionProps } from "./BasicIntegrationCard";
import { JobberIntegrationCard } from "./JobberIntegrationCard";
import { LACRMIntegrationCard } from "./LACRMIntegrationCard";
import { ServiceTitanIntegrationCard, ServiceTitanSettingsUpdate } from "./ServiceTitanIntegrationCard";
import { HousecallProIntegrationCard } from "./HousecallProIntegrationCard";
import { SurefireLocalIntegrationCard } from "./SurefireLocalIntegrationCard";
import { ZapierIntegrationCard } from "./ZapierIntegrationCard";
import { FieldPulseIntegrationCard } from "./FieldPulseIntegrationCard";
import { FieldRoutesIntegrationCard } from "./FieldRoutesIntegrationCard";
import { ServiceMinderIntegrationCard } from "./ServiceMinderIntegrationCard";
import { ContractorAppointmentsIntegrationCard } from "./ContractorAppointmentsIntegrationCard";
import { BoberdooIntegrationCard } from "./BoberdooIntegrationCard";
import { Free2GrowIntegrationCard } from "./Free2GrowIntegrationCard";
import { LeadPerfectionIntegrationCard } from "./LeadPerfectionIntegrationCard";
import { WebhookIntegrationCard } from "./WebhookIntegrationCard";
import { FacebookCapiIntegrationCard } from "./FacebookCapiIntegrationCard";
import { SearchLightIntegrationCard, SearchLightConnectionProps } from "./SearchLightIntegrationCard";

import style from "./style.scss";

export interface IntegrationListProps {
    /**
     * The app created in the onboarding that we are integrating to
     */
    app: Pick<OnboardingProps, "appId" | "organizationId" | "leadsContact">;
    /**
     * Disable all interactions
     */
    disabled?: boolean;
    /**
     * Only show one of the integrations
     */
    limitIntegrations?: string[];
    /**
     * It will purposely omit the integrations if provided
     */
    omitIntegrations?: string[];
    /**
     * Does the current tier support integrations
     */
    supportsIntegrations?: boolean;
    /**
     * Response query integrationStatus
     */
    data?: AppIntegrationDetails;
    /**
     * Add Integration dialog is active
     */
    active?: boolean;
    /**
     * Special mode where we show all the integration cards to allow you to pick them
     * and we do not display the upgrade plan message.
     */
    onboardingMode?: boolean;
    /**
     * Dialog cancel
     */
    onCancel?(): void;
    /**
     * User is connecting a specific integration
     */
    onConnect?(props: IntegrationConnectionProps): void;
    /**
     * User requested to disconnect the integration.
     *
     * @param type
     */
    onDisconnect?(type: IntegrationType): void;
    /**
     * User toggled one of the ServiceTitan ingestion settings on a linked integration.
     */
    onUpdateServiceTitanSettings?(settings: ServiceTitanSettingsUpdate): void;
    /**
     * Integrations currently selected.
     *
     * If provided, the list will not actually allow integration to be complete but only selected.
     */
    selected?: string[];
    /**
     * Returns the selected integrations
     * @param selected
     */
    onSelected?(selected: string[]): void;
}

export const IntegrationList: FC<IntegrationListProps> = ({
    app,
    data,
    disabled,
    limitIntegrations,
    omitIntegrations,
    onboardingMode,
    onSelected,
    onConnect,
    onDisconnect,
    onUpdateServiceTitanSettings,
    selected,
}: IntegrationListProps) => {
    const selectionMode = existsAndNotEmpty(selected);

    if (selectionMode) {
        // make sure we have a callback
        if (typeof onSelected !== "function") {
            throw new Error("onSelected must be a function when selected array is provided");
        }
    }

    // What does linked and active mean?
    // isLinked - User has set up a link to jobber
    // isActive - The link works

    const isBoberdooLinked: boolean = data?.boberdoo?.isLinked ?? false;
    const isBoberdooActive: boolean = data?.boberdoo?.isActive ?? false;
    const isBoberdooSelected: boolean | undefined = selected?.includes("boberdoo");
    const isFree2GrowLinked: boolean = data?.free2grow?.isLinked ?? false;
    const isFree2GrowActive: boolean = data?.free2grow?.isActive ?? false;
    const isFree2GrowSelected: boolean | undefined = selected?.includes("free2grow");
    const free2GrowReferenceId: string | undefined = data?.free2grow?.referenceId ?? undefined;

    const isLeadPerfectionLinked: boolean = data?.leadperfection?.isLinked ?? false;
    const isLeadPerfectionActive: boolean = data?.leadperfection?.isActive ?? false;
    const isLeadPerfectionSelected: boolean | undefined = selected?.includes("leadperfection");

    const isLACRMLinked: boolean = data?.lacrm?.isLinked;
    const isLACRMActive: boolean = data?.lacrm?.isActive;
    const isLACRMSelected: boolean | undefined = selected?.includes("lacrm");

    const isJobberLinked: boolean = data?.jobber?.isLinked;
    const isJobberActive: boolean = data?.jobber?.isActive;
    const isJobberSelected: boolean | undefined = selected?.includes("jobber");

    const isServiceTitanLinked: boolean = data?.servicetitan?.isLinked;
    const isServiceTitanActive: boolean = data?.servicetitan?.isActive;
    const isServiceTitanSelected: boolean | undefined = selected?.includes("servicetitan");

    const isHousecallProLinked: boolean = data?.housecallpro?.isLinked;
    const isHousecallProActive: boolean = data?.housecallpro?.isActive;
    const isHousecallProSelected: boolean | undefined = selected?.includes("housecallpro");

    const isFieldPulseLinked: boolean = data?.fieldpulse?.isLinked;
    const isFieldPulseActive: boolean = data?.fieldpulse?.isActive;
    const isFieldPulseSelected: boolean | undefined = selected?.includes("fieldpulse");

    const isFieldRoutesLinked: boolean = data?.fieldroutes?.isLinked;
    const isFieldRoutesActive: boolean = data?.fieldroutes?.isActive;
    const isFieldRoutesSelected: boolean | undefined = selected?.includes("fieldroutes");

    const isSurefireLocalLinked: boolean = data?.surefire?.isLinked;
    const isSurefireLocalActive: boolean = data?.surefire?.isActive;
    const isSurefireLocalSelected: boolean | undefined = selected?.includes("surefire");

    const isZapierLinked: boolean = false;
    const isZapierActive: boolean = false;
    const isZapierSelected: boolean | undefined = selected?.includes("zapier");

    const isServiceMinderLinked: boolean = data?.serviceminder?.isLinked;
    const isServiceMinderActive: boolean = data?.serviceminder?.isActive;
    const isServiceMinderSelected: boolean | undefined = selected?.includes("serviceminder");

    const isContractorAppointmentsLinked: boolean = data?.contractor_appointments?.isLinked;
    const isContractorAppointmentsActive: boolean = data?.contractor_appointments?.isActive;
    const isContractorAppointmentsSelected: boolean | undefined = selected?.includes("contractor_appointments");

    const isWebhookLinked: boolean = data?.webhook?.isLinked;
    const isWebhookActive: boolean = data?.webhook?.isActive;
    // Unset on any webhook connected before flat mode existed, so default it rather than
    // handing the card an undefined it would have to coerce anyway.
    const isWebhookFlattened: boolean = data?.webhook?.flattenPayload ?? false;
    const isWebhookSelected: boolean | undefined = selected?.includes("webhook");

    const isFacebookCapiLinked: boolean = data?.facebook_capi?.isLinked;
    const isFacebookCapiActive: boolean = data?.facebook_capi?.isActive;
    const isFacebookCapiSelected: boolean | undefined = selected?.includes("facebook_capi");

    // SearchLight is a feed config, not an integration(type:). The API only feeds when enabled AND an
    // account ID is stored, so "linked" is simply "enabled" — a stored-but-off config is offered in
    // the add dialog with the account ID prefilled rather than shown on the page as broken.
    const searchLightAccountId: string | undefined = data?.searchLightFeed?.accountId ?? undefined;
    const isSearchLightEnabled: boolean = data?.searchLightFeed?.enabled ?? false;
    const isSearchLightLinked: boolean = isSearchLightEnabled;
    const isSearchLightActive: boolean = isSearchLightEnabled;
    const isSearchLightSelected: boolean | undefined = selected?.includes("searchlight");

    const DEFAULT_INTEGRATIONS = [
        "boberdoo",
        "free2grow",
        "leadperfection",
        "lacrm",
        "jobber",
        "surefire",
        "servicetitan",
        "serviceminder",
        "housecallpro",
        "zapier",
        "fieldpulse",
        "fieldroutes",
        "contractor_appointments",
        "webhook",
        "facebook_capi",
        "searchlight",
    ];

    // first make sure limitIntegrations is included;

    let availableIntegrations: string[] = DEFAULT_INTEGRATIONS;

    if (existsAndNotEmpty(limitIntegrations)) {
        // if we have
        if (existsAndNotEmpty(omitIntegrations)) {
            // Create a set for faster lookup
            const omitSet = new Set(omitIntegrations);

            // Filter out the omitted integrations
            limitIntegrations = limitIntegrations.filter((integration) => {
                // if the integration is in the omit set, we should not include it
                if (omitSet.has(integration)) {
                    return false;
                }
                return true;
            });
        }

        // make sure at least one of the integrations in limit integrations is in the list of DEFAULTs
        const limitSet = new Set(limitIntegrations);

        const defaultSet = new Set(DEFAULT_INTEGRATIONS);

        const intersection = new Set([...limitSet].filter((x) => defaultSet.has(x)));

        if (intersection.size === 0) {
            availableIntegrations = DEFAULT_INTEGRATIONS;
        } else {
            availableIntegrations = [...intersection];
        }
    }

    if (existsAndNotEmpty(omitIntegrations)) {
        // Create a set for faster lookup
        const omitSet = new Set(omitIntegrations);

        // Filter out the omitted integrations
        availableIntegrations = availableIntegrations.filter((integration) => {
            if (omitSet.has(integration)) {
                return false;
            }
            return true;
        });
    }

    // one more check, if available integrations is empty, we should show all integrations
    if (!existsAndNotEmpty(availableIntegrations)) {
        availableIntegrations = DEFAULT_INTEGRATIONS;
    }

    const linkedIntegrations: string[] = [];

    if (isLACRMLinked) {
        linkedIntegrations.push("lacrm");
    }

    if (isJobberLinked) {
        linkedIntegrations.push("jobber");
    }

    if (isServiceTitanLinked) {
        linkedIntegrations.push("servicetitan");
    }

    if (isHousecallProLinked) {
        linkedIntegrations.push("housecallpro");
    }

    if (isFieldPulseLinked) {
        linkedIntegrations.push("fieldpulse");
    }

    if (isFieldRoutesLinked) {
        linkedIntegrations.push("fieldroutes");
    }

    if (isSurefireLocalLinked) {
        linkedIntegrations.push("surefire");
    }

    if (isServiceMinderLinked) {
        linkedIntegrations.push("serviceminder");
    }

    if (isContractorAppointmentsLinked) {
        linkedIntegrations.push("contractor_appointments");
    }

    if (isFree2GrowLinked) {
        linkedIntegrations.push("free2grow");
    }

    if (isBoberdooLinked) {
        linkedIntegrations.push("boberdoo");
    }

    if (isLeadPerfectionLinked) {
        linkedIntegrations.push("leadperfection");
    }

    if (isWebhookLinked) {
        linkedIntegrations.push("webhook");
    }

    if (isFacebookCapiLinked) {
        linkedIntegrations.push("facebook_capi");
    }

    if (isSearchLightLinked) {
        linkedIntegrations.push("searchlight");
    }

    //  const hasLinkedIntegration: boolean = existsAndNotEmpty(linkedIntegrations);

    const handleSelected = useCallback(
        (selected: string[]) => {
            if (typeof onSelected === "function") {
                onSelected(selected);
            }
        },
        [onSelected]
    );

    // console.log("IntegrationsList", { availableIntegrations, linkedIntegrations, hasLinkedIntegration });

    return (
        <div className={style.listContainer}>
            {((availableIntegrations.includes("jobber") && onboardingMode) || isJobberLinked) && (
                <JobberIntegrationCard
                    checked={isJobberSelected}
                    onClick={handleSelected.bind(null, ["jobber"])}
                    disabled={disabled}
                    appId={app?.appId}
                    isActive={isJobberActive}
                    isLinked={isJobberLinked}
                    onDisconnect={typeof onDisconnect === "function" ? onDisconnect.bind(this, "jobber") : undefined}
                />
            )}
            {((availableIntegrations.includes("servicetitan") && onboardingMode) || isServiceTitanLinked) && (
                <ServiceTitanIntegrationCard
                    checked={isServiceTitanSelected}
                    onClick={handleSelected.bind(null, ["servicetitan"])}
                    disabled={disabled}
                    appId={app?.appId}
                    isActive={isServiceTitanActive}
                    isLinked={isServiceTitanLinked}
                    leadImport={data?.servicetitan?.leadImport}
                    outcomeTracking={data?.servicetitan?.outcomeTracking}
                    appV2Accepted={data?.servicetitan?.invoicesRead ?? undefined}
                    onConnect={onConnect}
                    onDisconnect={
                        typeof onDisconnect === "function" ? onDisconnect.bind(this, "servicetitan") : undefined
                    }
                    onUpdateSettings={onUpdateServiceTitanSettings}
                />
            )}
            {((availableIntegrations.includes("serviceminder") && onboardingMode) || isServiceMinderLinked) && (
                <ServiceMinderIntegrationCard
                    checked={isServiceMinderSelected}
                    onClick={handleSelected.bind(null, ["serviceminder"])}
                    disabled={disabled}
                    appId={app?.appId}
                    isActive={isServiceMinderActive}
                    isLinked={isServiceMinderLinked}
                    onConnect={onConnect}
                    onDisconnect={
                        typeof onDisconnect === "function" ? onDisconnect.bind(this, "serviceminder") : undefined
                    }
                />
            )}
            {((availableIntegrations.includes("fieldpulse") && onboardingMode) || isFieldPulseLinked) && (
                <FieldPulseIntegrationCard
                    checked={isFieldPulseSelected}
                    onClick={handleSelected.bind(null, ["fieldpulse"])}
                    disabled={disabled}
                    appId={app?.appId}
                    isActive={isFieldPulseActive}
                    isLinked={isFieldPulseLinked}
                    onConnect={onConnect}
                    onDisconnect={
                        typeof onDisconnect === "function" ? onDisconnect.bind(this, "fieldpulse") : undefined
                    }
                />
            )}
            {((availableIntegrations.includes("fieldroutes") && onboardingMode) || isFieldRoutesLinked) && (
                <FieldRoutesIntegrationCard
                    checked={isFieldRoutesSelected}
                    onClick={handleSelected.bind(null, ["fieldroutes"])}
                    disabled={disabled}
                    appId={app?.appId}
                    isActive={isFieldRoutesActive}
                    isLinked={isFieldRoutesLinked}
                    onConnect={onConnect}
                    onDisconnect={
                        typeof onDisconnect === "function" ? onDisconnect.bind(this, "fieldroutes") : undefined
                    }
                />
            )}
            {((availableIntegrations.includes("housecallpro") && onboardingMode) || isHousecallProLinked) && (
                <HousecallProIntegrationCard
                    checked={isHousecallProSelected}
                    onClick={handleSelected.bind(null, ["housecallpro"])}
                    disabled={disabled}
                    appId={app?.appId}
                    isActive={isHousecallProActive}
                    isLinked={isHousecallProLinked}
                    onConnect={onConnect}
                    onDisconnect={
                        typeof onDisconnect === "function" ? onDisconnect.bind(this, "housecallpro") : undefined
                    }
                />
            )}
            {((availableIntegrations.includes("surefire") && onboardingMode) || isSurefireLocalLinked) && (
                <SurefireLocalIntegrationCard
                    checked={isSurefireLocalSelected}
                    onClick={handleSelected.bind(null, ["surefire"])}
                    disabled={disabled}
                    appId={app?.appId}
                    isActive={isSurefireLocalActive}
                    isLinked={isSurefireLocalLinked}
                    onConnect={onConnect}
                    onDisconnect={typeof onDisconnect === "function" ? onDisconnect.bind(this, "surefire") : undefined}
                />
            )}
            {availableIntegrations.includes("zapier") && onboardingMode && (
                <ZapierIntegrationCard
                    checked={isZapierSelected}
                    onClick={handleSelected.bind(null, ["zapier"])}
                    disabled={disabled}
                    appId={app?.appId}
                    isActive={isZapierActive}
                    isLinked={isZapierLinked}
                    onDisconnect={typeof onDisconnect === "function" ? onDisconnect.bind(this, "zapier") : undefined}
                />
            )}
            {((availableIntegrations.includes("lacrm") && onboardingMode) || isLACRMLinked) && (
                <LACRMIntegrationCard
                    checked={isLACRMSelected}
                    onClick={handleSelected.bind(null, ["lacrm"])}
                    disabled={disabled}
                    appId={app?.appId}
                    isActive={isLACRMActive}
                    isLinked={isLACRMLinked}
                    onDisconnect={typeof onDisconnect === "function" ? onDisconnect.bind(this, "lacrm") : undefined}
                />
            )}
            {((availableIntegrations.includes("free2grow") && onboardingMode) || isFree2GrowLinked) && (
                <Free2GrowIntegrationCard
                    checked={isFree2GrowSelected}
                    onClick={handleSelected.bind(null, ["free2grow"])}
                    disabled={disabled}
                    appId={app?.appId}
                    isActive={isFree2GrowActive}
                    isLinked={isFree2GrowLinked}
                    referenceId={free2GrowReferenceId}
                    onConnect={onConnect}
                    onDisconnect={typeof onDisconnect === "function" ? onDisconnect.bind(this, "free2grow") : undefined}
                />
            )}
            {((availableIntegrations.includes("boberdoo") && onboardingMode) || isBoberdooLinked) && (
                <BoberdooIntegrationCard
                    checked={isBoberdooSelected}
                    onClick={handleSelected.bind(null, ["boberdoo"])}
                    disabled={disabled}
                    appId={app?.appId}
                    isActive={isBoberdooActive}
                    isLinked={isBoberdooLinked}
                    onConnect={onConnect}
                    onDisconnect={typeof onDisconnect === "function" ? onDisconnect.bind(this, "boberdoo") : undefined}
                />
            )}
            {((availableIntegrations.includes("leadperfection") && onboardingMode) || isLeadPerfectionLinked) && (
                <LeadPerfectionIntegrationCard
                    checked={isLeadPerfectionSelected}
                    onClick={handleSelected.bind(null, ["leadperfection"])}
                    disabled={disabled}
                    appId={app?.appId}
                    isActive={isLeadPerfectionActive}
                    isLinked={isLeadPerfectionLinked}
                    onConnect={onConnect}
                    onDisconnect={
                        typeof onDisconnect === "function" ? onDisconnect.bind(this, "leadperfection") : undefined
                    }
                />
            )}
            {((availableIntegrations.includes("contractor_appointments") && onboardingMode) ||
                isContractorAppointmentsLinked) && (
                <ContractorAppointmentsIntegrationCard
                    checked={isContractorAppointmentsSelected}
                    onClick={handleSelected.bind(null, ["contractor_appointments"])}
                    disabled={disabled}
                    appId={app?.appId}
                    isActive={isContractorAppointmentsActive}
                    isLinked={isContractorAppointmentsLinked}
                    onConnect={onConnect}
                    onDisconnect={
                        typeof onDisconnect === "function"
                            ? onDisconnect.bind(this, "contractor_appointments")
                            : undefined
                    }
                />
            )}
            {((availableIntegrations.includes("webhook") && onboardingMode) || isWebhookLinked) && (
                <WebhookIntegrationCard
                    checked={isWebhookSelected}
                    onClick={handleSelected.bind(null, ["webhook"])}
                    disabled={disabled}
                    appId={app?.appId}
                    isActive={isWebhookActive}
                    isLinked={isWebhookLinked}
                    flattenPayload={isWebhookFlattened}
                    onConnect={onConnect}
                    onDisconnect={typeof onDisconnect === "function" ? onDisconnect.bind(this, "webhook") : undefined}
                />
            )}
            {((availableIntegrations.includes("facebook_capi") && onboardingMode) || isFacebookCapiLinked) && (
                <FacebookCapiIntegrationCard
                    checked={isFacebookCapiSelected}
                    onClick={handleSelected.bind(null, ["facebook_capi"])}
                    disabled={disabled}
                    appId={app?.appId}
                    isActive={isFacebookCapiActive}
                    isLinked={isFacebookCapiLinked}
                    onConnect={onConnect}
                    onDisconnect={
                        typeof onDisconnect === "function" ? onDisconnect.bind(this, "facebook_capi") : undefined
                    }
                />
            )}
            {((availableIntegrations.includes("searchlight") && onboardingMode) || isSearchLightLinked) && (
                <SearchLightIntegrationCard
                    checked={isSearchLightSelected}
                    onClick={handleSelected.bind(null, ["searchlight"])}
                    disabled={disabled}
                    appId={app?.appId}
                    organizationId={app?.organizationId}
                    isActive={isSearchLightActive}
                    isLinked={isSearchLightLinked}
                    accountId={searchLightAccountId}
                    enabled={isSearchLightEnabled}
                    onConnect={onConnect}
                    // There is no disconnect(type:) for SearchLight; turning the feed off is the same
                    // save with enabled false. The stored account ID is kept so re-enabling is one click.
                    // Gated on isSearchLightLinked as well as onConnect: the add-integration
                    // picker passes onConnect but not onDisconnect, which is what keeps an X off
                    // every other unlinked card. Without the link check this one showed a live X
                    // on a feed that was never connected, and it saved an empty account ID.
                    onDisconnect={
                        typeof onConnect === "function" && isSearchLightLinked
                            ? () =>
                                  onConnect({
                                      integration: "searchlight-feed",
                                      accountId: searchLightAccountId ?? "",
                                      enabled: false,
                                  } as SearchLightConnectionProps)
                            : undefined
                    }
                />
            )}
        </div>
    );
};
